// Session AI — turn URLs inside agent-typed text into working links.
//
// The event description is free text an agent types in the agent portal. Links
// pasted into it rendered as plain text everywhere, so a staff member could see
// a URL but not open it — and on a phone there is no practical way to copy it
// out of a paragraph.
//
// ⚠️ NO dangerouslySetInnerHTML, deliberately. This returns an ARRAY OF REACT
// NODES, so every non-link fragment is still escaped by React as text. An agent
// typing `<script>` gets a literal `<script>` on screen, not a script tag.
//
// ⚠️ Only `http://`, `https://` and bare `www.` are matched, so the regex
// cannot produce a `javascript:` href. That is the security property — do not
// widen the pattern to "any scheme" without thinking it through.

// No lookbehind: Safari on older iOS does not support it, and this runs on
// staff phones we do not control.
const URL_RE = /(https?:\/\/[^\s<>()[\]]+|www\.[^\s<>()[\]]+)/gi;

const linkStyle = {
  color: 'var(--accent)',
  fontWeight: 600,
  textDecoration: 'underline',
  // A long URL must wrap rather than force the whole sheet to scroll sideways.
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

export function linkify(text) {
  const s = text == null ? '' : String(text);
  if (!s) return null;

  const out = [];
  let last = 0;
  let key = 0;
  let m;

  URL_RE.lastIndex = 0;
  while ((m = URL_RE.exec(s)) !== null) {
    let url = m[0];

    // Don't swallow sentence punctuation: "see https://x.co.za." should link
    // the URL and leave the full stop as text. Closing brackets too.
    const trail = url.match(/[.,;:!?)\]}'"]+$/);
    if (trail) url = url.slice(0, url.length - trail[0].length);
    if (!url) continue;   // lastIndex has already advanced, so no infinite loop

    if (m.index > last) out.push(s.slice(last, m.index));

    const href = /^www\./i.test(url) ? 'https://' + url : url;
    out.push(
      <a
        key={'l' + (key++)}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={e => e.stopPropagation()}   // never trigger a parent card tap
        style={linkStyle}
      >
        {url}
      </a>
    );
    last = m.index + url.length;
  }

  if (last < s.length) out.push(s.slice(last));
  return out.length ? out : s;
}
